// =======================================================================
// Excel Export — Wave 4.5. Reuses the real CostSpace Pro visual pattern
// (ExcelJS, navy/green/red/amber palette, sHdr/sData/sTot row stylers)
// discovered in the original ExcelExport.jsx, WITHOUT the obsolete
// "QUBEX Cost Intelligence" branding — this workbook identifies itself
// as CostSpace Estimation throughout.
//
// STRICT RULE: every number written here is read verbatim from Supabase
// (estimate_revision_calculation_chain, boq_item_pricing_status). No
// financial calculation is performed in this file.
// =======================================================================
import ExcelJS from 'exceljs'
import { supabase } from './supabase'
import { deriveFrozenPricingStatuses, frozenPricingStatusUnavailable } from './frozenPricingStatus'

const C = {
  navy: 'FF1B3A6B', navyLight: 'FF2C4F82',
  green: 'FF1A7A4A', greenBg: 'FFF0FDF4',
  red: 'FFB91C1C', redBg: 'FFFEF2F2',
  amber: 'FF9A3412', amberBg: 'FFFFF7ED',
  white: 'FFFFFFFF', darkTx: 'FF0F1017', mutedTx: 'FF6B7280',
  border: 'FFE5E7EB', altRow: 'FFF9FAFB', totRow: 'FF1B3A6B',
  neutralBg: 'FFF3F4F6', neutralTx: 'FF6B7280',
}

const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } })
const fnt = (bold = false, sz = 10, argb = C.darkTx) => ({ name: 'Calibri', size: sz, bold, color: { argb } })
const border = () => {
  const b = { style: 'thin', color: { argb: C.border } }
  return { top: b, left: b, bottom: b, right: b }
}
const alignC = { horizontal: 'left', vertical: 'middle' }

const numFmt = '#,##0.00'
const qtyFmt = '#,##0.###'
const intFmt = '#,##0'

const STATUS_STYLE = {
  priced: { bg: C.greenBg, tx: C.green, label: 'Priced' },
  unpriced: { bg: C.redBg, tx: C.red, label: 'Unpriced' },
  incomplete: { bg: C.amberBg, tx: C.amber, label: 'Incomplete' },
  zero_priced: { bg: C.amberBg, tx: C.amber, label: 'Zero Priced' },
  excluded: { bg: C.neutralBg, tx: C.neutralTx, label: 'Excluded' },
}

function styleHeaderRow(row, bg = C.navy) {
  row.height = 22
  row.eachCell((c) => { c.fill = fill(bg); c.font = fnt(true, 10, C.white); c.border = border(); c.alignment = { horizontal: 'center', vertical: 'middle' } })
}
function styleDataRow(row, alt = false) {
  row.height = 18
  row.eachCell((c) => { c.fill = fill(alt ? C.altRow : C.white); c.font = fnt(false, 10, C.darkTx); c.border = border(); c.alignment = alignC })
}
function styleTotalRow(row) {
  row.height = 22
  row.eachCell((c) => { c.fill = fill(C.totRow); c.font = fnt(true, 10, C.white); c.border = border(); c.alignment = alignC })
}

function addTitleBar(ws, text, mergeTo) {
  ws.mergeCells(`A1:${mergeTo}1`)
  const c = ws.getCell('A1')
  c.value = text
  c.fill = fill(C.navy)
  c.font = fnt(true, 14, C.white)
  c.alignment = { horizontal: 'center', vertical: 'middle' }
  ws.getRow(1).height = 32
}
function addInfoBar(ws, lines, mergeTo) {
  ws.mergeCells(`A2:${mergeTo}2`)
  const c = ws.getCell('A2')
  c.value = lines
  c.font = fnt(false, 9, C.mutedTx)
  c.alignment = { horizontal: 'center', vertical: 'middle' }
  ws.getRow(2).height = 16
  ws.getRow(3).height = 6
}

// -----------------------------------------------------------------------
// Fetch all data needed for export, straight from the existing
// calculation-chain Views — no computation here.
// -----------------------------------------------------------------------
async function fetchRevisionExportData(tenderId, revisionId) {
  const [tenderRes, revRes, ccRes] = await Promise.all([
    // currency added: this export has no React/UI context once opened in
    // Excel, so every monetary column must carry its own currency label
    // explicitly -- there is no "Tender Currency" badge elsewhere on the
    // page to imply it, unlike the in-app screens.
    supabase.from('tender').select('name, client_name, location, currency').eq('id', tenderId).single(),
    supabase.from('estimate_revision').select('revision_number, label, status, pricing_date').eq('id', revisionId).single(),
    supabase.from('canonical_cost_code').select('id, code'),
  ])
  if (tenderRes.error) throw new Error(tenderRes.error.message)
  if (revRes.error) throw new Error(revRes.error.message)
  if (ccRes.error) throw new Error(ccRes.error.message)
  const ccMap = Object.fromEntries((ccRes.data || []).map((c) => [c.id, c.code]))

  if (revRes.data?.status === 'frozen') {
    // Frozen: estimate_revision_calculation_chain / boq_item_pricing_status
    // / boq_item_selling_price / boq_item_fully_loaded_pricing all return
    // empty once the live source tables are cleared post-freeze (proven
    // directly). Build the same shapes from the immutable freeze_snapshot
    // instead -- direct_cost/selling_price/effective_markup_pct are
    // verbatim per-item fields already captured at freeze time;
    // fully_loaded_selling_price and pricing_status are the same
    // presentation-level derivations already used and proven exact in
    // RevisionWorkspacePage.jsx (BoqTab) and UnpricedItemsPage.jsx --
    // replayed here, not a new financial rule.
    const snapRes = await supabase
      .from('freeze_snapshot')
      .select('direct_cost_total, ho_overhead_total, markup_total, tender_price_total, full_boq_data, full_indirect_data')
      .eq('estimate_revision_id', revisionId)
      .maybeSingle()
    if (snapRes.error) throw new Error(snapRes.error.message)
    if (!snapRes.data) throw new Error('This revision is marked frozen but no snapshot record could be found.')

    const boqData = Array.isArray(snapRes.data.full_boq_data) ? snapRes.data.full_boq_data : []
    const indirect = snapRes.data.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
    const rateBuildupLines = Array.isArray(indirect.rate_buildup_lines) ? indirect.rate_buildup_lines : []
    // Same guard as UnpricedItemsPage.jsx: a pre-contract frozen revision
    // (no rate_buildup_lines captured) cannot be classified without
    // guessing -- leave pricing_status null rather than misreport every
    // item as 'unpriced'.
    const statusUnavailable = frozenPricingStatusUnavailable(boqData, rateBuildupLines)
    const statusById = statusUnavailable ? {} : Object.fromEntries(deriveFrozenPricingStatuses(boqData, rateBuildupLines).map((s) => [s.boq_item_id, s]))

    const eligibleTotal = boqData.reduce((s, it) => { const dc = Number(it.direct_cost) || 0; return dc > 0 ? s + dc : s }, 0)
    const gso = Number(indirect.general_site_overhead_total) || 0
    const ho = Number(snapRes.data.ho_overhead_total) || 0
    const gc = Number(indirect.general_contingency_total) || 0

    const items = boqData.map((it) => {
      const dc = Number(it.direct_cost) || 0
      const allocPct = (dc > 0 && eligibleTotal > 0) ? dc / eligibleTotal : 0
      const fullyLoadedCost = dc + (Number(it.boq_specific_site_overhead) || 0) + (Number(it.boq_specific_risk) || 0) + allocPct * gso + allocPct * ho + allocPct * gc
      const markupPct = Number(it.effective_markup_pct) || 0

      const s = statusById[it.id]

      return {
        boq_item_id: it.id, item_no: it.item_no, description: it.description, unit: it.unit,
        resolved_quantity: it.resolved_quantity, pricing_status: s?.pricing_status ?? null, line_count: s?.line_count ?? null, null_line_count: s?.null_line_count ?? null,
        selling_price: it.selling_price, direct_cost: dc, fully_loaded_selling_price: fullyLoadedCost * (1 + markupPct / 100),
        package_name: it.package_name || null, cost_code: ccMap[it.canonical_cost_code_id] || null,
      }
    })

    return {
      tender: tenderRes.data, revision: revRes.data,
      chain: {
        direct_cost_total: snapRes.data.direct_cost_total, ho_overhead_total: snapRes.data.ho_overhead_total,
        markup_total: snapRes.data.markup_total, tender_price_total: snapRes.data.tender_price_total,
        general_site_overhead_total: gso, general_contingency_total: gc,
      },
      items,
    }
  }

  const [chainRes, pricingRes, sellingRes, fullyLoadedRes] = await Promise.all([
    supabase.from('estimate_revision_calculation_chain').select('*').eq('estimate_revision_id', revisionId).maybeSingle(),
    supabase.from('boq_item_pricing_status').select('*').eq('estimate_revision_id', revisionId).order('item_no'),
    supabase.from('boq_item_selling_price').select('boq_item_id, direct_cost').eq('estimate_revision_id', revisionId),
    // Phase 1 -- derived Fully Loaded Pricing, read verbatim from the
    // approved read-only view. This is what "Selling Price" in this
    // report now means (Direct + Specific Indirect + Allocated
    // General Indirect + Markup), matching the BOQ screen's corrected
    // Final Unit Rate/price decision -- not the intermediate
    // Direct + Markup price.
    supabase.from('boq_item_fully_loaded_pricing').select('boq_item_id, fully_loaded_selling_price').eq('estimate_revision_id', revisionId),
  ])
  if (chainRes.error) throw new Error(chainRes.error.message)
  if (pricingRes.error) throw new Error(pricingRes.error.message)
  if (sellingRes.error) throw new Error(sellingRes.error.message)
  if (fullyLoadedRes.error) throw new Error(fullyLoadedRes.error.message)

  // Package (boq_item.package_name) and Cost Code
  // (boq_item.canonical_cost_code_id) live on boq_item itself, not on
  // any of the pricing views above -- fetched separately here, read
  // only, never written by this report.
  const { data: boqItemsRaw, error: boqError } = await supabase
    .from('boq_item')
    .select('id, package_name, canonical_cost_code_id')
    .eq('estimate_revision_id', revisionId)
  if (boqError) throw new Error(boqError.message)

  const directCostMap = Object.fromEntries((sellingRes.data || []).map((s) => [s.boq_item_id, s.direct_cost]))
  const flMap = Object.fromEntries((fullyLoadedRes.data || []).map((f) => [f.boq_item_id, f.fully_loaded_selling_price]))
  const classifyMap = Object.fromEntries((boqItemsRaw || []).map((b) => [b.id, b]))

  const items = (pricingRes.data || []).map((it) => {
    const classify = classifyMap[it.boq_item_id] || {}
    return {
      ...it,
      direct_cost: directCostMap[it.boq_item_id] ?? null,
      fully_loaded_selling_price: flMap[it.boq_item_id] ?? null,
      package_name: classify.package_name || null,
      cost_code: ccMap[classify.canonical_cost_code_id] || null,
    }
  })

  return { tender: tenderRes.data, revision: revRes.data, chain: chainRes.data, items }
}

// -----------------------------------------------------------------------
// BOQ Presentation Report -- a formatted, print-style Excel report
// (Tender Summary sheet + styled BOQ Detail sheet with status badges,
// colors, totals). This is NOT the CostSpace-compatible data export
// (that is handleExportBoq / "Export to CostSpace" in
// RevisionWorkspacePage.jsx, a separate plain-data file matching the
// CostSpace BOQ.jsx importer contract).
//
// Function name kept as exportBoqToExcel (NOT renamed) so existing
// callers keep working without any React-side edit. Only the visible
// button label needs to change to avoid confusion -- that is a
// one-line text edit wherever this is called from, not a function
// rename.
// -----------------------------------------------------------------------
export async function exportBoqToExcel(tenderId, revisionId) {
  const { tender, revision, chain, items } = await fetchRevisionExportData(tenderId, revisionId)

  const wb = new ExcelJS.Workbook()
  wb.creator = 'CostSpace Estimation'
  wb.created = new Date()

  const nowStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  // ===================== SHEET 1: PROJECT / REVISION SUMMARY =====================
  const s1 = wb.addWorksheet('Summary')
  s1.columns = [{ width: 30 }, { width: 24 }]
  addTitleBar(s1, 'COSTSPACE ESTIMATION — TENDER SUMMARY', 'B')
  addInfoBar(s1, `Generated ${nowStr}`, 'B')

  const summaryRows = [
    ['Project / Tender', tender.name],
    ['Client', tender.client_name || '—'],
    ['Location', tender.location || '—'],
    ['Currency', tender.currency || 'Not set'],
    ['Revision', `Rev ${revision.revision_number} — ${revision.label}`],
    ['Status', revision.status],
    ['Pricing Date', revision.pricing_date],
  ]
  let r = 4
  for (const [label, value] of summaryRows) {
    const row = s1.getRow(r)
    row.getCell(1).value = label
    row.getCell(2).value = value
    row.getCell(1).font = fnt(true, 10, C.mutedTx)
    row.getCell(2).font = fnt(false, 10, C.darkTx)
    row.height = 18
    r++
  }
  r += 1

  const finRows = chain ? [
    ['Direct Cost', Number(chain.direct_cost_total)],
    ['Site Overhead', Number(chain.general_site_overhead_total) + Number(chain.boq_specific_site_overhead_total) + Number(chain.package_specific_site_overhead_total)],
    ['HO Overhead', Number(chain.ho_overhead_total)],
    ['Contingency', Number(chain.general_contingency_total) + Number(chain.boq_specific_risk_total) + Number(chain.package_specific_risk_total)],
    ['Markup', Number(chain.markup_total)],
  ] : []

  const finHdr = s1.getRow(r)
  finHdr.getCell(1).value = 'FINANCIAL SUMMARY'
  s1.mergeCells(`A${r}:B${r}`)
  finHdr.getCell(1).fill = fill(C.navy)
  finHdr.getCell(1).font = fnt(true, 10, C.white)
  finHdr.height = 20
  r++

  for (const [label, value] of finRows) {
    const row = s1.getRow(r)
    row.getCell(1).value = label
    row.getCell(2).value = value
    row.getCell(2).numFmt = numFmt
    row.getCell(1).font = fnt(false, 10, C.darkTx)
    row.getCell(2).font = fnt(false, 10, C.darkTx)
    row.height = 17
    r++
  }

  if (chain) {
    const tpRow = s1.getRow(r)
    tpRow.getCell(1).value = 'TENDER PRICE'
    tpRow.getCell(2).value = Number(chain.tender_price_total)
    tpRow.getCell(2).numFmt = numFmt
    styleTotalRow(tpRow)
  } else {
    s1.getRow(r).getCell(1).value = 'No calculation data available for this revision.'
    s1.getRow(r).getCell(1).font = fnt(false, 10, C.mutedTx)
  }

  // ===================== SHEET 2: BOQ DETAIL =====================
  const s2 = wb.addWorksheet('BOQ Detail')
  s2.columns = [
    { width: 12 }, { width: 42 }, { width: 14 }, { width: 14 }, { width: 10 }, { width: 14 },
    { width: 16 }, { width: 18 }, { width: 18 }, { width: 16 },
  ]
  addTitleBar(s2, 'BOQ DETAIL', 'J')
  addInfoBar(s2, `${tender.name} — Rev ${revision.revision_number} — Generated ${nowStr}`, 'J')

  const hdrRow = s2.getRow(4)
  ;['Item No.', 'Description', 'Package', 'Cost Code', 'Unit', 'Quantity', 'Status', `Direct Cost (${tender.currency || '—'})`, `Selling Price (${tender.currency || '—'})`, `Final Unit Rate (${tender.currency || '—'})`].forEach((h, i) => { hdrRow.getCell(i + 1).value = h })
  styleHeaderRow(hdrRow)
  s2.views = [{ state: 'frozen', ySplit: 4 }]

  let totalDirect = 0
  let totalSelling = 0
  items.forEach((it, idx) => {
    const row = s2.getRow(5 + idx)
    row.getCell(1).value = it.item_no || '—'
    row.getCell(2).value = it.description
    // Package/Cost Code -- pure classification, read from boq_item
    // directly (no derivation, no financial meaning).
    row.getCell(3).value = it.package_name || '—'
    row.getCell(4).value = it.cost_code || '—'
    row.getCell(5).value = it.unit
    row.getCell(6).value = Number(it.resolved_quantity)
    row.getCell(6).numFmt = qtyFmt
    const statusCfg = STATUS_STYLE[it.pricing_status] || STATUS_STYLE.unpriced
    row.getCell(7).value = statusCfg.label
    row.getCell(7).fill = fill(statusCfg.bg)
    row.getCell(7).font = fnt(true, 9, statusCfg.tx)
    row.getCell(7).alignment = { horizontal: 'center', vertical: 'middle' }

    const dc = it.pricing_status === 'excluded' ? null : (it.direct_cost ?? 0)
    // "Selling Price" here is the FINAL price (Fully Loaded Selling
    // Price -- Direct + Specific Indirect + Allocated General
    // Indirect + Markup), matching the BOQ screen's corrected
    // decision, not the intermediate Direct + Markup price.
    const sp = it.pricing_status === 'excluded' ? null : (Number(it.fully_loaded_selling_price) || 0)
    const qty = Number(it.resolved_quantity) || 0
    const finalUnitRate = (sp != null && qty > 0) ? sp / qty : null
    if (dc != null) { row.getCell(8).value = Number(dc); row.getCell(8).numFmt = numFmt; totalDirect += Number(dc) }
    if (sp != null) { row.getCell(9).value = Number(sp); row.getCell(9).numFmt = numFmt; totalSelling += Number(sp) }
    if (finalUnitRate != null) { row.getCell(10).value = Number(finalUnitRate); row.getCell(10).numFmt = numFmt }

    row.height = 17
    row.eachCell((c, colNum) => {
      if (colNum === 7) return // status cell already styled above
      c.border = border()
      c.font = c.font || fnt(false, 10, C.darkTx)
      c.fill = c.fill || fill(idx % 2 === 0 ? C.white : C.altRow)
      c.alignment = c.alignment || alignC
    })
  })

  const totalRowIdx = 5 + items.length
  const totalRow = s2.getRow(totalRowIdx)
  totalRow.getCell(1).value = 'TOTAL'
  s2.mergeCells(`A${totalRowIdx}:G${totalRowIdx}`)
  totalRow.getCell(8).value = totalDirect
  totalRow.getCell(8).numFmt = numFmt
  totalRow.getCell(9).value = totalSelling
  totalRow.getCell(9).numFmt = numFmt
  styleTotalRow(totalRow)

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const fileName = `${tender.name.replace(/[^a-z0-9]/gi, '_')}_Rev${revision.revision_number}_BOQ.xlsx`
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = fileName; a.click()
  URL.revokeObjectURL(url)
}

// -----------------------------------------------------------------------
// Downloadable import template — same visual language, with an
// instructions section explaining required/optional fields and behavior.
// -----------------------------------------------------------------------
export async function downloadImportTemplate() {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'CostSpace Estimation'
  wb.created = new Date()

  const ws = wb.addWorksheet('BOQ Import Template')
  ws.columns = [{ width: 12 }, { width: 42 }, { width: 10 }, { width: 14 }]
  addTitleBar(ws, 'COSTSPACE — BOQ IMPORT TEMPLATE', 'D')

  const hdrRow = ws.getRow(3)
  ;['Item No', 'Description', 'Unit', 'Quantity'].forEach((h, i) => { hdrRow.getCell(i + 1).value = h })
  styleHeaderRow(hdrRow)

  const example = ws.getRow(4)
  example.getCell(1).value = '1.01'
  example.getCell(2).value = 'Excavation for pile caps and raft foundation'
  example.getCell(3).value = 'm3'
  example.getCell(4).value = 3200
  styleDataRow(example)

  const infoStart = 6
  const infoLines = [
    'INSTRUCTIONS',
    'Required fields: Description, Unit, Quantity.',
    'Optional field: Item No — if left blank, the row will always be created as a new item (never matched for update).',
    'Duplicate Item No within this file will be flagged as Ambiguous and skipped automatically.',
    'If an Item No already exists in the target revision, you will be asked to Create New or Update Existing during import.',
    'Updating an existing item that already has pricing (Rate Build-Up) will show a warning before proceeding.',
    'This import never modifies Rate Build-Up, First-Principles, Crew, Productivity, or Resource Rate data — only BOQ master fields (Item No, Description, Unit, Quantity).',
  ]
  infoLines.forEach((line, i) => {
    const row = ws.getRow(infoStart + i)
    row.getCell(1).value = line
    ws.mergeCells(`A${infoStart + i}:D${infoStart + i}`)
    row.getCell(1).font = i === 0 ? fnt(true, 10, C.navy) : fnt(false, 9, C.mutedTx)
    row.height = 16
  })

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = 'CostSpace_BOQ_Import_Template.xlsx'; a.click()
  URL.revokeObjectURL(url)
}
